// Implements: F-IDN-012, F-UIX-009
/**
 * The profile photo (v3 `.av-edit`): the 64px face, "Subir foto" / "Cambiar foto" and, with a photo,
 * "Usar mis iniciales". Pure presentation: the file goes up through `onPick`, the removal through
 * `onRemove`; what the server answers comes back as `imageUrl`.
 *
 * The file picker is a real `<input type="file">` labelled by the visible button, so a keyboard or
 * a screen reader reaches it as one control; it accepts only the three types the server keeps.
 */

import { useId, useRef } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { initialsOf } from "../../../shared/ui/atoms/Avatar";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import styles from "./AvatarEditor.module.css";
import shared from "./account.module.css";

export const AVATAR_ACCEPT = "image/png,image/jpeg,image/webp";

export interface AvatarEditorProps {
  readonly name: string;
  readonly imageUrl: string | null;
  readonly busy: boolean;
  readonly onPick: (file: File) => void;
  readonly onRemove: () => void;
}

export function AvatarEditor({ name, imageUrl, busy, onPick, onRemove }: AvatarEditorProps): ReactElement {
  const { t } = useTranslation();
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const initials = initialsOf(name);
  const pickLabel = t(imageUrl === null ? "account.profile.photoUpload" : "account.profile.photoChange");

  return (
    <div className={styles.editor}>
      <span
        className={styles.face}
        role="img"
        aria-label={imageUrl === null ? t("account.profile.initialsAlt", { initials }) : t("account.profile.photoAlt")}
      >
        {imageUrl === null ? initials : <img className={styles.picture} src={imageUrl} alt={""} />}
      </span>
      <div className={styles.actions}>
        <div className={styles.buttons}>
          <input
            ref={input}
            id={inputId}
            className={styles.file}
            type="file"
            accept={AVATAR_ACCEPT}
            aria-label={pickLabel}
            tabIndex={-1}
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              // The same file picked twice in a row must still reach `onPick`.
              event.target.value = "";
              if (file !== undefined) onPick(file);
            }}
          />
          <Button
            variant="secondary"
            disabled={busy}
            loading={busy}
            onClick={() => {
              input.current?.click();
            }}
          >
            <Icon name="upload" size="sm" />
            {pickLabel}
          </Button>
          {imageUrl === null ? null : (
            <Button variant="ghost" disabled={busy} onClick={onRemove}>
              {t("account.profile.photoInitials")}
            </Button>
          )}
        </div>
        <p className={shared.help}>{t("account.profile.photoHelp")}</p>
      </div>
    </div>
  );
}
